/**
 * API Request Deduplication (In-flight only, NO response caching)
 * 
 * Prevents the same GET endpoint from being called multiple times
 * simultaneously. If a request for URL X is already in-flight,
 * subsequent calls to URL X return the SAME promise instead of
 * firing another network request.
 * 
 * Once the request completes, the next call will always go to the server.
 * This means data is ALWAYS fresh — no stale cache risk.
 *
 * This solves the "47 API calls on page load" problem because
 * multiple React components mounting simultaneously all try to
 * fetch the same endpoints within milliseconds of each other.
 */

const inflight = new Map();  // dedupKey -> Promise

// Key must include query params — concurrent GETs to the same URL with
// DIFFERENT params are different requests (URL-only keys cross-contaminate
// responses, e.g. bell thumbnail lookups for multiple notifications).
const dedupKey = (url, config) => {
  const params = config?.params;
  if (!params) return url;
  try {
    const sorted = Object.keys(params).sort().map(k => `${k}=${params[k]}`).join('&');
    return `${url}?${sorted}`;
  } catch {
    return `${url}?${Date.now()}`;
  }
};

/**
 * Wrap an axios instance to deduplicate concurrent GET requests.
 * - GET: If same URL+params is already in-flight, return the same Promise
 * - POST/PUT/DELETE: Always pass through (no deduplication)
 */
export function withDeduplication(axiosInstance) {
  const originalGet = axiosInstance.get.bind(axiosInstance);

  axiosInstance.get = function(url, config) {
    const key = dedupKey(url, config);
    // Return in-flight request if one exists for this URL+params
    const existing = inflight.get(key);
    if (existing) {
      return existing;
    }

    // Fail fast instead of hanging the UI: default 45s timeout on GETs
    // (callers can still override via their own config.timeout)
    const effectiveConfig = { timeout: 45000, ...(config || {}) };

    // Make the actual request
    const promise = originalGet(url, effectiveConfig).finally(() => {
      // Clean up once complete — next call will always hit the server
      inflight.delete(key);
    });

    inflight.set(key, promise);
    return promise;
  };

  return axiosInstance;
}
